import { api } from "./client";

export interface AnalysisFlightOption {
  id: number;
  session_name: string;
  owner_label: string | null;
  map_name: string | null;
  date_display: string;
  time_window_display: string;
  duration_display: string;
  distance_display: string;
  avg_speed_display: string;
  smoothness_display: string;
  status: string;
  status_label: string;
}

export interface AnalysisPagination {
  page: number;
  page_size: number;
  page_count: number;
  total: number;
}

export interface AnalysisFlightsOut {
  missions: string[];
  selected_mission: string | null;
  mission_flight_rows: AnalysisFlightOption[];
  users: AnalysisUser[];
  filters: { user_id: number | null };
  pagination: AnalysisPagination;
}

export interface AnalysisSelectedRouteRow {
  flight_id: number;
  session_name: string;
  owner_label: string | null;
  map_name: string | null;
  date_display: string;
  time_window_display: string;
  duration_display: string;
  distance_display: string;
  avg_speed_display: string;
  smoothness_display: string;
  avg_deviation_display: string;
  max_deviation_display: string;
  color: string;
}

export interface AnalysisRoute {
  flight_id: number;
  session_name: string;
  color: string;
  path: number[][];
}

export interface AnalysisDeviationSeries {
  flight_id: number;
  session_name: string;
  color: string;
  series: number[][];
}

export interface AnalysisStructure {
  id?: number;
  source_structure_id?: number | string;
  class_name?: string;
  structure_kind?: string;
  size_m?: number;
  latitude?: number;
  longitude?: number;
  altitude_m?: number;
  yaw_deg?: number;
  pitch_deg?: number;
  roll_deg?: number;
}

export interface AnalysisHomePoint {
  latitude: number;
  longitude: number;
  altitude_m?: number;
  label?: string;
}

export interface AnalysisPayload {
  routes: AnalysisRoute[];
  average_route: number[][];
  deviation_series: AnalysisDeviationSeries[];
  scenario_structures: AnalysisStructure[];
  home_point: AnalysisHomePoint | null;
}

export interface AnalysisOverview {
  mission_flight_count: number;
  selected_count: number;
  average_distance_display: string;
  average_duration_display: string;
  average_deviation_display: string;
  owner_scope: string;
}

export interface AnalysisUser {
  id: number;
  username: string;
  flight_count: number;
}

export interface AnalysisOut {
  selected_mission: string | null;
  selected_flight_ids: number[];
  analysis_notice: string | null;
  selected_route_rows: AnalysisSelectedRouteRow[];
  analysis_payload: AnalysisPayload;
  overview: AnalysisOverview;
}

export interface CompareBundleSummary {
  session_name: string;
  color: string;
  flight_id: number;
  owner_label?: string | null;
  map_name?: string | null;
  date_display?: string | null;
  duration_seconds?: number | null;
  distance_m?: number | null;
  avg_speed_kmh?: number | null;
  max_speed_kmh?: number | null;
  max_altitude_m?: number | null;
  smoothness_score?: number | null;
  peak_g?: number | null;
  battery_spent_pct?: number | null;
  avg_deviation_m?: number | null;
  max_deviation_m?: number | null;
}

export interface CompareBundle {
  summary: CompareBundleSummary;
  map2d_path: number[][];
  map3d_path: number[][];
  timed_points: Array<Record<string, unknown>>;
  normalized_route: Array<Record<string, unknown>>;
}

export interface CompareMetricRow {
  label: string;
  description: string;
  values: string[];
  delta: string;
}

export interface CompareRoutes2D {
  session_name: string;
  color: string;
  path: number[][];
}

export interface CompareRoutes3D {
  session_name: string;
  color: string;
  path: number[][];
  points: Array<Record<string, unknown>>;
}

export interface ComparePayload {
  routes2d: CompareRoutes2D[];
  routes3d: CompareRoutes3D[];
  average_route: number[][];
  deviation_series: AnalysisDeviationSeries[];
  scenario_structures: AnalysisStructure[];
  home_point: AnalysisHomePoint | null;
}

export interface AnalysisCompareMissionFlight {
  id: number;
  session_name: string;
  owner_label: string | null;
  date_display: string;
}

export interface AnalysisCompareOut {
  missions: string[];
  selected_mission: string | null;
  mission_flights: AnalysisCompareMissionFlight[];
  users: AnalysisUser[];
  filters: { user_id: number | null };
  selected_flight_ids: number[];
  max_flights: number;
  compare_bundles: CompareBundle[];
  metric_rows: CompareMetricRow[];
  payload: ComparePayload;
  compare_notice: string | null;
}

function buildQueryString(
  params: Record<string, string | number | undefined | null | number[]>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, String(v)));
    } else {
      qs.set(key, String(value));
    }
  }
  return qs.toString() ? `?${qs.toString()}` : "";
}

export function getAnalysisFlights(params: {
  mission?: string;
  user_id?: number;
  page?: number;
  page_size?: number;
}) {
  return api.get<AnalysisFlightsOut>(`/api/analysis/flights${buildQueryString(params)}`);
}

export function getAnalysis(params: {
  mission?: string;
  user_id?: number;
  flight_id?: number[];
}) {
  return api.get<AnalysisOut>(`/api/analysis${buildQueryString(params)}`);
}

export function getAnalysisCompare(params: {
  mission?: string;
  user_id?: number;
  flight_id?: number[];
}) {
  return api.get<AnalysisCompareOut>(`/api/analysis/compare${buildQueryString(params)}`);
}
